import { useCallback, useEffect, useState } from 'react'
import { MdSave } from 'react-icons/md'
import { useUser } from '../context/UserContext'
import {
  guardarPresupuestoGerencia,
  listarPresupuestoGerencia,
  quitarPresupuestoGerencia,
} from '../api/apiConfig'
import { formatNumberEsCL } from '../utils/numberFormat'

const MESES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
]

const unwrap = (response) => {
  let data = response
  if (response?.statusCode && response.body != null) {
    data = typeof response.body === 'string' ? JSON.parse(response.body) : response.body
  }
  return data && typeof data === 'object' ? data : {}
}

const parseNumero = (raw) => {
  const trimmed = String(raw ?? '').trim().replace(/\s/g, '').replace(',', '.')
  if (trimmed === '') return null
  const n = Number(trimmed)
  return Number.isFinite(n) ? n : null
}

const MetaPresupuestoGerencia = () => {
  const { user } = useUser()
  const ahora = new Date()
  const [anio, setAnio] = useState(ahora.getFullYear())
  const [mes, setMes] = useState(ahora.getMonth() + 1)
  const [valor, setValor] = useState('')
  const [sumaReal, setSumaReal] = useState(null)
  const [filas, setFilas] = useState([])
  const [hayValor, setHayValor] = useState(false)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')
  const [messageType, setMessageType] = useState('')

  const cargar = useCallback(async () => {
    if (!user) return
    setLoading(true)
    setMessage('')
    try {
      const data = unwrap(await listarPresupuestoGerencia(user, { anio, mes }))
      const guardado = data.valor
      setHayValor(guardado != null && guardado !== '')
      setValor(guardado == null || guardado === '' ? '' : String(guardado).replace('.', ','))
      setSumaReal(data.suma_real ?? null)
      setFilas(Array.isArray(data.filas) ? data.filas : [])
    } catch (err) {
      setMessage(err?.message || 'No se pudo cargar el presupuesto de Gerencia')
      setMessageType('error')
    } finally {
      setLoading(false)
    }
  }, [user, anio, mes])

  useEffect(() => {
    cargar()
  }, [cargar])

  const guardar = async () => {
    const n = parseNumero(valor)
    if (n == null || n < 0) {
      setMessage('Ingresa un número válido, en millones')
      setMessageType('error')
      return
    }
    setSaving(true)
    setMessage('')
    try {
      await guardarPresupuestoGerencia(user, { anio, mes, meta_vol_factoring: n })
      setMessage(`Gerencia verá MM$ ${formatNumberEsCL(n)} en ${MESES[mes - 1]} ${anio}.`)
      setMessageType('success')
      await cargar()
    } catch (err) {
      setMessage(err?.message || 'No se pudo guardar')
      setMessageType('error')
    } finally {
      setSaving(false)
    }
  }

  const quitar = async () => {
    setSaving(true)
    setMessage('')
    try {
      await quitarPresupuestoGerencia(user, { anio, mes })
      setMessage('Sin valor propio, Gerencia vuelve a ver la suma de la tabla metas.')
      setMessageType('success')
      await cargar()
    } catch (err) {
      setMessage(err?.message || 'No se pudo quitar')
      setMessageType('error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="p-6">
      <div className="flex items-baseline gap-4 mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Meta presupuesto Gerencia</h1>
        <p className="text-gray-600 text-sm">
          Valor de Meta Factoring que ve Gerencia en Inicio. No cambia la tabla metas.
        </p>
      </div>

      <div className="seccion-datos max-w-3xl">
        <p className="text-sm text-gray-600 mb-4">
          Tú, como administrador, sigues viendo en Inicio la suma real de
          {' '}<code className="text-xs bg-gray-100 px-1 rounded">META_MET_VOL</code>.
          Gerencia ve este número mientras exista para el mes en curso.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
          <label className="text-sm text-gray-700">
            Año
            <input
              type="number"
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              value={anio}
              onChange={(e) => setAnio(Number(e.target.value))}
            />
          </label>
          <label className="text-sm text-gray-700">
            Mes
            <select
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              value={mes}
              onChange={(e) => setMes(Number(e.target.value))}
            >
              {MESES.map((nombre, i) => (
                <option key={nombre} value={i + 1}>{nombre}</option>
              ))}
            </select>
          </label>
          <label className="text-sm text-gray-700">
            Meta que verá Gerencia (MM$)
            <input
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              value={valor}
              onChange={(e) => setValor(e.target.value)}
              placeholder="Ej: 50935"
              disabled={loading}
            />
          </label>
        </div>

        <p className="text-sm text-gray-700 mb-4">
          Suma real de la tabla en {MESES[mes - 1]} {anio}, la que ves tú en Inicio:{' '}
          <strong>
            {sumaReal == null ? '—' : `MM$ ${formatNumberEsCL(sumaReal)}`}
          </strong>
        </p>

        <div className="flex flex-wrap gap-2">
          <button type="button" className="btn-success py-2 px-4 text-sm" onClick={guardar} disabled={saving || loading}>
            <span className="inline-flex items-center gap-1">
              <MdSave size={16} />
              {saving ? 'Guardando...' : 'Guardar'}
            </span>
          </button>
          <button
            type="button"
            className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
            onClick={quitar}
            disabled={saving || loading || !hayValor}
          >
            Quitar este mes
          </button>
        </div>

        {message && (
          <div className={`mt-4 p-3 rounded text-sm ${messageType === 'success' ? 'bg-green-100 text-green-800 border border-green-300' : 'bg-red-100 text-red-800 border border-red-300'}`}>
            {message}
          </div>
        )}

        {filas.length > 0 && (
          <div className="mt-6">
            <h2 className="text-sm font-semibold text-gray-800 mb-2">Valores guardados en {anio}</h2>
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className="text-left py-2">Mes</th>
                  <th className="text-right py-2">Gerencia ve</th>
                </tr>
              </thead>
              <tbody>
                {filas.map((fila) => (
                  <tr key={fila.MES_MET} className="border-t border-gray-100">
                    <td className="py-2">{MESES[Number(fila.MES_MET) - 1] || fila.MES_MET}</td>
                    <td className="py-2 text-right">MM$ {formatNumberEsCL(fila.META_VOL_FACTORING)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

export default MetaPresupuestoGerencia
